import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";
import { i18n } from "@/i18n";
import { cloneDeep, filter, groupBy, mapValues, pickBy } from "lodash-es";
import { splitUid, toUid, type Uid } from "@/helpers/utility/Id";
import { showAlert } from "@core/ui/alert/alert";
import { clampNumber } from "@/helpers/utility/MathHelper";
import { ensureInt } from "@/helpers/utility/Type";
import Text from "@/helpers/utility/Text";
import { calculateExpressionValues } from "@editor/items/index/indexFunctions";
import { calculateFlowBranches } from "@editor/items/flow/flowBranchFunctions";
import { useSimulationViewStore } from "./simulationView.store";
import { useMomentumStore } from "@editor/items/momentum/momentums.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useSimulationMessage } from "@core/ui/simulationMessage.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useElements } from "@editor/items/element/elements";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import Impulse from "@editor/items/impulse/Impulse";
import type Effect from "@editor/items/effect/Effect";
import type Simulation from "@editor/items/simulation/Simulation";
import type { ExpressionValue } from "@editor/items/expression/expressionFunctions";
import type { PreventedAction, SimulationRoundItem, UnpayableAction } from "@editor/items/simulationRound/SimulationRoundItem";
import type { EndReason } from "@editor/items/simulation/SimulationItem";
import type { ImpulseItem } from "@editor/items/impulse/ImpulseItem";
import type { MomentumItem } from "@editor/items/momentum/MomentumItem";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IncidentItem } from "@editor/items/incident/IncidentItem";
import type { ActionItem } from "@editor/items/action/ActionItem";
import type { EffectItem } from "@editor/items/effect/EffectItem";
import type { FlowItem } from "@editor/items/flow/FlowItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";

export const useSimulatedRoundStore = defineStore("model/simulatedRound", () => {
  const visualizationSettings = reactive({
    visibleMomentumMessages: true,
    visibleIncidentMessages: true,
    visibleActionMessages: true,
    visibleHighlights: true,
    animationSpeed: 1,
  });

  const sceneTree = useSceneTree();

  // ========
  // ELEMENT VALUES

  const elementValues = ref({} as Record<Uid<string>, ExpressionValue>);

  function setElementValues(values: Record<Uid<string>, ExpressionValue>) {
    elementValues.value = cloneDeep(values);
  }

  // ========
  // IMPULSES

  const impulses = ref(new Map<Uid<string>, Impulse>());
  const appliedImpulses = ref(new Map<Uid<string>, Impulse>());

  function addImpulseSource(affectedUid: Uid<string>, impulseSource: ImpulseItem["sourceValues"][0]) {
    if (!impulses.value.has(affectedUid)) {
      impulses.value.set(affectedUid, new Impulse({
        type: "impulse",
        pointsToAdd: 0,
        valueBefore: Number(elementValues.value[affectedUid]?.value || 0),
        valueAfter: undefined,
        sourceValues: []
      }));
    }

    const impulse = impulses.value.get(affectedUid)!;
    impulse.sourceValues.push(impulseSource);
  }

  function applyImpulse(uid: Uid<string>) {
    const impulse = impulses.value.get(uid);
    if (impulse == null) return;
    const elementValue = elementValues.value[uid];
    if (elementValue == null) return;

    for (const source of impulse.sourceValues) {
      if (source.value != null) {
        elementValue.value = clampNumber(Number(elementValue.value) + source.value, elementValue.minValue, elementValue.maxValue);
      }
      if (source.valueToSet != null) {
        elementValue.value = clampNumber(source.valueToSet, elementValue.minValue, elementValue.maxValue);
      }
    }

    appliedImpulses.value.set(uid, impulse);
    impulses.value.delete(uid);
  }

  function applyImpulses() {
    for (const uid of impulses.value.keys()) {
      applyImpulse(uid);
    }
  }

  function resetImpulses() {
    impulses.value.clear();
    appliedImpulses.value.clear();
  }

  // ========
  // HIGHLIGHTS

  const highlights = ref(new Set<Uid<"target" | "resource" | "index" | "relation" | "flow" | "flowBranch" | "momentum">>());
  const impulseHighlights = ref(new Set<Uid<string>>());

  // ========
  // ROUND MANAGEMENT

  const roundIndex = ref(0);

  function loadRound(round: SimulationRoundItem, simulator: Simulation) {
    highlights.value.clear();
    impulseHighlights.value.clear();

    roundIndex.value = round.index;
    setElementValues(round.startElementValues);

    recalculateIndices(simulator);

    resetImpulses();

    if (round.played) {
      // add first half impulses
      const roundImpulses = round.impulses || {
        target: {},
        resource: {}
      };
      for (const type of ["target", "resource"] as const) {
        for (const id in roundImpulses[type]) {
          const impulse = roundImpulses[type][id];
          const uid = toUid(type, parseInt(id));
          for (const source of impulse.sourceValues) {
            if (source.type == "incident" || source.type == "relation" || source.type == "momentum" || source.type == "delayed_action" || source.type == "delayed_incident") {
              addImpulseSource(uid, source);
            }
          }
        }
      }
    }
  }


  /**
   * Visualizes the first half of the round.
   * Handle incidents, impact relations, momentums, incidents, pending effects.
   */
  async function visualizeFirstHalf(simulator: Simulation, waitFactor = 1) {
    if (simulator.complete) return;

    const round = simulator.rounds[roundIndex.value];
    if (round == null) return;

    recalculateIndices(simulator);
    await speedWait(3000);

    await visualizeRelations(simulator);
    await visualizeMomentums(simulator);
    await visualizeIncidents(simulator);
    await visualizePendingEffects(simulator);
  }

  async function visualizeSecondHalf(simulator: Simulation) {
    recalculateIndices(simulator);

    await visualizeActions(simulator);
    await visualizeScheduledEffects(simulator);
    await speedWait(300);

    applyImpulses();
    recalculateIndices(simulator);
    await speedWait(2000);
  
    await visualizeFlows(simulator);
    applyImpulses();
    recalculateIndices(simulator, true);
    await speedWait(1000);
  }

  async function visualizeRelations(simulator: Simulation) {
    const round = simulator.rounds[roundIndex.value];
    const roundImpulses = round.impulses || {
      target: {},
      resource: {}
    };

    // show ALL TARGET RELATION impulse transfers
    const impulsesFromTargetRelations = mapValues(roundImpulses.target, (impulse) => {
      return impulse.sourceValues.filter((source) => source.type == "relation");
    });

    for (const targetId in impulsesFromTargetRelations) {
      const relationImpulses = impulsesFromTargetRelations[targetId];
      if (relationImpulses.length == 0) continue;

      const uid = toUid("target", parseInt(targetId));
      const treeNode = sceneTree.elementNodes.get(uid);
      const visible = !!treeNode?.visible;

      if (visible) {
        highlights.value.add(uid);
        for (const source of relationImpulses) {
          highlights.value.add(toUid("relation", source.id));
        }

        await waitHighlight(1000);

        impulseHighlights.value.add(uid);
      }

      for (const source of relationImpulses) {
        addImpulseSource(uid, source);
      }

      if (visible) {
        await waitHighlight(4000);

        highlights.value.delete(uid);
        impulseHighlights.value.delete(uid);
        for (const source of relationImpulses) {
          highlights.value.delete(toUid("relation", source.id));
        }
      }
    }

    // show ALL RESOURCE RELATION impulse transfers
    const impulsesFromResourceRelations = mapValues(roundImpulses.resource, (impulse) => {
      return impulse.sourceValues.filter((source) => source.type == "relation");
    });
    for (const eId in impulsesFromResourceRelations) {
      const relationImpulses = impulsesFromResourceRelations[eId];
      if (relationImpulses.length == 0) continue;

      const uid = toUid("resource", parseInt(eId));
      const treeNode = sceneTree.elementNodes.get(uid);
      const visible = !!treeNode?.visible;

      if (visible) {
        highlights.value.add(uid);
        for (const source of relationImpulses) {
          highlights.value.add(toUid("relation", source.id));
        }
        
        await waitHighlight(1000);

        impulseHighlights.value.add(uid);
      }

      for (const source of relationImpulses) {
        addImpulseSource(uid, source);
      }

      if (visible) {
        await waitHighlight(4000);

        highlights.value.delete(uid);
        impulseHighlights.value.delete(uid);
        for (const source of relationImpulses) {
          highlights.value.delete(toUid("relation", source.id));
        }
      }
    }
  }

  async function visualizeMomentums(simulator: Simulation) {
    const round = simulator.rounds[roundIndex.value];
    const roundImpulses = round.impulses || {
      target: {},
      resource: {}
    };

    // show MOMENTUM popups
    const targetImpulsesFromMomentums = filter(roundImpulses.target, (impulse) =>
      impulse.sourceValues.find((source) => source.type == "momentum") != null
    );
    const resourceImpulsesFromMomentums = filter(roundImpulses.resource, (impulse) =>
      impulse.sourceValues.find((source) => source.type == "momentum") != null
    );
    const impulsesFromMomentums = [
      ...targetImpulsesFromMomentums,
      ...resourceImpulsesFromMomentums
    ];
    if (impulsesFromMomentums.length == 0) return;

    const momentumStore = useMomentumStore();
    const targetStore = useTargetStore();
    const resourceStore = useResourceStore();

    const elements = {
      target: targetStore.items,
      resource: resourceStore.items
    };

    for (const impulse of impulsesFromMomentums) {
      const momentumImpulse = getSourcesByType(impulse, "momentum")[0];
      const momentumId = momentumImpulse.id;
      const momentum = momentumStore.items[ensureInt(momentumId)];

      const element = elements[momentum.elementType][momentum.elementId];
      const elementNode = sceneTree.elementNodes.get(element.uid);
      const visible = !!elementNode?.visible;

      if (visible) {
        highlights.value.add(element.uid);
        highlights.value.add(momentum.uid);
      }

      await showMomentumPopup({ momentum, element, round });

      if (visible) {
        impulseHighlights.value.add(element.uid);
  
        await waitHighlight(1500);
      }

      addImpulseSource(element.uid, momentumImpulse);

      if (visible) {
        await waitHighlight(3000);

        impulseHighlights.value.delete(element.uid);
        highlights.value.delete(momentum.uid);
        highlights.value.delete(element.uid);
        await waitHighlight(500);
      }
    }
  }

  async function visualizeIncidents(simulator: Simulation) {
    const round = simulator.rounds[roundIndex.value];
    const roundImpulses = round.impulses || {
      target: {},
      resource: {}
    };

    // show incidents
    const incidentStore = useIncidentStore();
    for (const incidentReason of round.activatedIncidents) {
      const incident = incidentStore.items[incidentReason.incidentId];
      if (incident?.visibility == "hidden") continue;
      const affectedUids = getAffectedItemIds(incident);

      for (const uid of affectedUids) {
        highlights.value.add(uid);
      }

      await showIncidentPopup({ incident, round });

      await waitHighlight(2000);

      for (const uid of affectedUids) {
        const { type, id } = splitUid(uid);
        impulseHighlights.value.add(uid);
        const impulse = roundImpulses[type]?.[id];
        const source = getSource(impulse, "incident", incident.id);
        if (source) {
          addImpulseSource(uid, source);
        }
      }

      await waitHighlight(2500);

      for (const uid of affectedUids) {
        impulseHighlights.value.delete(uid);
      }
      await waitHighlight(500);
    }
  }

  async function visualizePendingEffects(simulator: Simulation) {
    const round = simulator.rounds[roundIndex.value];
    const previousRound = simulator.rounds?.[roundIndex.value - 1];
    const roundImpulses = round.impulses || {
      target: {},
      resource: {}
    };

    if (previousRound?.pendingEffects) {
      const pendingEffectGroups = mapValues(
        groupBy(previousRound.pendingEffects, "activatorType"),
        (effectGroup) => groupBy(effectGroup, "activatorId")
      );

      for (const activatorType in pendingEffectGroups) {
        for (const activatorId in pendingEffectGroups[activatorType]) {
          const effects = pendingEffectGroups[activatorType][activatorId] ?? [];
          const affectedUids = getEffectsAffectedUids(effects);

          for (const uid of affectedUids) {
            highlights.value.add(uid);
            impulseHighlights.value.add(uid);

            const { type, id } = splitUid(uid);

            const impulse = roundImpulses[type]?.[id];
            const source = getSource(impulse, "delayed_" + activatorType, ensureInt(activatorId));

            if (source) {
              addImpulseSource(uid, source);
            }
          }
          
          await waitHighlight(4500);

          for (const uid of affectedUids) {
            highlights.value.delete(uid);
            impulseHighlights.value.delete(uid);
          }
          await waitHighlight(500);
        }
      }
    }
  }

  async function visualizeActions(simulator: Simulation) {
    const round = simulator.rounds[roundIndex.value];
    const roundActionPlan = simulator.actionPlans[roundIndex.value];

    // show played actions
    const plannedActionIds = roundActionPlan?.plannedActionIds ?? [];

    // ========
    // ACTIONS

    const actionStore = useActionStore();
    for (const actionId of plannedActionIds) {
      if (actionId in round.preventedActions) {
        const preventedAction = round.preventedActions[actionId];
        await showPreventedActionPopup({ preventedAction });
        await waitHighlight(500);
        continue;
      }

      if (actionId in round.notPayableActions) {
        const payment = round.notPayableActions[actionId];
        await showUnpayableActionPopup({
          unpayableAction: payment
        });
        await waitHighlight(500);
        continue;
      }

      const action = actionStore.items[actionId];
      const affectedUids = getAffectedItemIds(action);

      for (const uid of affectedUids) {
        highlights.value.add(uid);
      }

      await showActionPopup({ action, round: round });

      await waitHighlight(1000);

      for (const uid of affectedUids) {
        impulseHighlights.value.add(uid);
        const { type, id } = splitUid(uid);
        const impulse = round.impulses[type]?.[id];
        const source = getSource(impulse, "action", action.id);
        if (source) {
          addImpulseSource(uid, source);
        }
      }

      await waitHighlight(4500);

      for (const uid of affectedUids) {
        highlights.value.delete(uid);
        impulseHighlights.value.delete(uid);
      }
      await waitHighlight(500);
    }
  }

  async function visualizeScheduledEffects(simulator: Simulation) {
    const round = simulator.rounds[roundIndex.value];
    const roundActionPlan = simulator.actionPlans[roundIndex.value];

    // show scheduled effects
    const scheduledEffects = roundActionPlan?.scheduledEffects ?? [];
    for (const effect of scheduledEffects) {
      const affectedUid = toUid(effect.affectedType, effect.affectedId);
      const impulse = round.impulses[effect.affectedType]?.[effect.affectedId];
      if (impulse == null) continue;
      const source = getSource(impulse, "scheduledEffect", effect.id);
      if (source) {
        addImpulseSource(affectedUid, source);
        highlights.value.add(affectedUid);
        impulseHighlights.value.add(affectedUid);
      }
      
      await waitHighlight(4500);

      highlights.value.delete(affectedUid);
      
      await waitHighlight(500);
    }
  }

  function recalculateIndices(simulator: Simulation, done = false) {
    const rounds: SimulationRoundItem[] = simulator.rounds;
    const round = rounds[roundIndex.value];

    const localElementValues: Record<Uid<"target" | "resource" | "index" | "flow" | "flowBranch">, ExpressionValue> = {};
    for (const key in elementValues.value) {
      const uid = key as Uid<"target" | "resource" | "index">;
      if (uid.startsWith("target_") || uid.startsWith("resource_")) {
        localElementValues[uid] = {
          ...elementValues.value[uid]
        };
      }
    }

    const randomValues = done ? round.randomValues : round.startRandomValues;

    const localRound = { ...round, elementValues: localElementValues };
    const localRounds = [...rounds];
    localRounds[roundIndex.value] = localRound;
    const parseContext = {
      ...cloneDeep(simulator.getCommonParseContext()),
      rounds: localRounds,
      roundIndex: roundIndex.value,
      randomValues: randomValues
    };

    // INDICES
    const indexValues = calculateExpressionValues(
      simulator.netItems.index,
      parseContext
    );
    for (const key in indexValues) {
      const indexUid = key as Uid<"index">;
      localElementValues[indexUid] = {
        ...indexValues[indexUid]
      };
    }

    // FLOWS
    const flowValues = calculateExpressionValues(simulator.flows, parseContext);
    for (const key in flowValues) {
      const flowUid = key as Uid<"flow">;
      localElementValues[flowUid] = {
        ...flowValues[flowUid]
      };
    }

    const activeFlows = [];
    for (const flowId in simulator.flows) {
      const flow = simulator.flows[flowId];
      if (flow.interval != null) {
        let roundCount = roundIndex.value + 1;
        if (done) roundCount++;
        if (roundCount % flow.interval != 0) {
          localElementValues[flow.uid].value = 0;
          for (const branch of flow.branches) {
            localElementValues[branch.uid] = {
              value: 0,
              minValue: 0,
              maxValue: 0,
              unit: "",
              unitValid: true,
              valid: true
            };
          }
        } else {
          activeFlows.push(flow);
        }
      } else {
        activeFlows.push(flow);
      }
    }

    const activeFlowBranches: { [flowId: number]: FlowBranchItem[] } = {};
    for (const flow of activeFlows) {
      activeFlowBranches[flow.id] = flow.branches;
    }

    const calculatedBranches = calculateFlowBranches(
      pickBy(simulator.flows, (flow) => activeFlowBranches[flow.id] != null),
      activeFlowBranches,
      localElementValues
    );

    for (const calculatedBranch of calculatedBranches.values()) {
      const branchValue = calculatedBranch.value;
      const branchUid = calculatedBranch.uid;
      const value = {
        value: branchValue,
        minValue: branchValue,
        maxValue: branchValue,
        unit: "",
        unitValid: true,
        valid: true
      };
      localElementValues[branchUid] = value;
    }

    elementValues.value = { ...localElementValues };
  }

  async function visualizeFlows(simulator: Simulation) {
    const round = simulator.rounds[roundIndex.value];

    const newImpulses = round.impulses || {
      target: {},
      resource: {}
    };
  
    // show flows to resources
    const flowResourceImpulses = filter(
      newImpulses.resource,
      (impulse) =>
        impulse.sourceValues.find(
          (source) => source.type == "flowBranch"
        ) != null
    );
    // show flows to targets
    const flowTargetImpulses = filter(
      newImpulses.target,
      (impulse) =>
        impulse.sourceValues.find(
          (source) => source.type == "flowBranch"
        ) != null
    );
  
    const flowImpulses = [
      ...flowResourceImpulses,
      ...flowTargetImpulses
    ];
  
    const flowBranchStore = useFlowBranchStore();
    const flowBranches = flowBranchStore.items;
    const flows = useFlowStore().items;
    const outBranchesByElement = flowBranchStore.outgoingByElementUid;
    const inBranchesByElement = flowBranchStore.incomingByElementUid;
  
    const activatedFlows = flowImpulses.reduce((activatedFlows, impulse) => {
      const flowBranchImpulses = getSourcesByType(
        impulse,
        "flowBranch"
      );
  
      for (const branchImpulse of flowBranchImpulses) {
        // don't highlight the branch if nothing changes
        if (branchImpulse.value == 0) continue;
  
        const branch = flowBranches[ensureInt(branchImpulse.id)];
        const branchElementNode = sceneTree.elementNodes.get(toUid(branch.elementType, branch.elementId));
        const flow = flows[branch.flowId];
        if (!activatedFlows[flow.id]) {
          activatedFlows[flow.id] = {
            flow,
            branches: [branch],
            impulses: [impulse],
            visible: !!branchElementNode?.visible
          };
        } else {
          activatedFlows[flow.id].branches.push(branch);
          activatedFlows[flow.id].impulses.push(impulse);
          activatedFlows[flow.id].visible = activatedFlows[flow.id].visible || !!branchElementNode?.visible;
        }
      }
  
      return activatedFlows;
    }, {} as { [flowId: number]: {
      flow: FlowItem;
      branches: FlowBranchItem[];
      impulses: ImpulseItem[];
      visible: boolean;
    }});
  
    // Order the flow branches:
    // flows from the void first
    // then flows from elements with no inflows
    // then rest
    const sortedActivatedFlows = sortFlowActivations(Object.values(activatedFlows), inBranchesByElement, outBranchesByElement);
  
    for (const activatedFlow of sortedActivatedFlows) {
      if (activatedFlow.visible) {
        highlights.value.add(activatedFlow.flow.uid);
    
        for (const branch of activatedFlow.branches) {
          highlights.value.add(branch.uid);
          highlights.value.add(toUid(branch.elementType, branch.elementId));
        }
    
        await waitHighlight(1000);
      }
  
      for (const branch of activatedFlow.branches) {
        const impulse = round.impulses[branch.elementType]?.[branch.elementId];
        const source = getSource(impulse, "flowBranch", branch.id);
        if (source) {
          const uid = toUid(branch.elementType, branch.elementId);
          addImpulseSource(uid, source);
          
          if (activatedFlow.visible) {
            await waitHighlight(1000);
          }
        }
      }

      if (activatedFlow.visible) {
        await waitHighlight(3500);
    
        for (const branch of activatedFlow.branches) {
          highlights.value.delete(branch.uid);
          highlights.value.delete(toUid(branch.elementType, branch.elementId));
        }

        highlights.value.delete(activatedFlow.flow.uid);

        await waitHighlight(500);
      }
    }
  }
  

  async function speedWait(time: number) {
    await wait(time * visualizationSettings.animationSpeed);
  }
  async function waitHighlight(time: number) {
    if (visualizationSettings.visibleHighlights) {
      await speedWait(time);
    }
  }

  const legacyImpulses = computed(() => {
    const legacyImpulses = { target: {}, resource: {} } as {
      target: Record<number, Impulse>;
      resource: Record<number, Impulse>;
    };
    
    impulses.value.forEach((impulse, uid) => {
      const { type, id } = splitUid(uid);
      legacyImpulses[type as "target" | "resource"][id] = impulse;
    });

    return legacyImpulses;
  });

  const allSeparatedImpulses = computed(() => {
    const separatedImpulses = { target: {}, resource: {} } as {
      target: Record<number, Impulse>;
      resource: Record<number, Impulse>;
    };
    
    appliedImpulses.value.forEach((impulse, uid) => {
      const { type, id } = splitUid(uid);
      separatedImpulses[type as "target" | "resource"][id] = impulse;
    });

    impulses.value.forEach((impulse, uid) => {
      const { type, id } = splitUid(uid);
      separatedImpulses[type as "target" | "resource"][id] = impulse;
    });

    return separatedImpulses;
  });

  return {
    elementValues,
    highlights,
    impulseHighlights,
    impulses: legacyImpulses,
    allImpulses: allSeparatedImpulses,
    resetImpulses,
    loadRound,
    visualizeFirstHalf,
    visualizeSecondHalf,
    visualizationSettings,
    showEndCard
  };
});

function sortFlowActivations(activatedFlows: {
  flow: FlowItem;
  branches: FlowBranchItem[];
  impulses: ImpulseItem[];
  visible: boolean;
}[], inBranchesByElement: Record<string, FlowBranchItem[]>, outBranchesByElement: Record<string, FlowBranchItem[]>): {
  flow: FlowItem;
  branches: FlowBranchItem[];
  impulses: ImpulseItem[];
  visible: boolean;
}[] {
  type ActivatedFlow = {
    flow: FlowItem;
    branches: FlowBranchItem[];
    impulses: ImpulseItem[];
    visible: boolean;
  };

  if (activatedFlows.length <= 1) return activatedFlows;

  const byFlowId = new Map<number, ActivatedFlow>();
  const sourceElementsByFlow = new Map<number, Set<string>>();
  const sinkElementsByFlow = new Map<number, Set<string>>();

  for (const activatedFlow of activatedFlows) {
    byFlowId.set(activatedFlow.flow.id, activatedFlow);

    const sourceElements = new Set<string>();
    const sinkElements = new Set<string>();
    for (const branch of activatedFlow.branches) {
      const elementUid = `${branch.elementType}_${branch.elementId}`;
      if (branch.branchType == "source") {
        sourceElements.add(elementUid);
      } else if (branch.branchType == "sink") {
        sinkElements.add(elementUid);
      }
    }

    sourceElementsByFlow.set(activatedFlow.flow.id, sourceElements);
    sinkElementsByFlow.set(activatedFlow.flow.id, sinkElements);
  }

  // Build flow dependencies on shared elements:
  // If flow A has a sink on element E and flow B has a source on E, then B depends on A.
  const sourceFlowIdsByElement = new Map<string, Set<number>>();
  const sinkFlowIdsByElement = new Map<string, Set<number>>();
  for (const [flowId, sourceElements] of sourceElementsByFlow) {
    for (const elementUid of sourceElements) {
      if (!sourceFlowIdsByElement.has(elementUid)) {
        sourceFlowIdsByElement.set(elementUid, new Set<number>());
      }
      sourceFlowIdsByElement.get(elementUid)!.add(flowId);
    }
  }
  for (const [flowId, sinkElements] of sinkElementsByFlow) {
    for (const elementUid of sinkElements) {
      if (!sinkFlowIdsByElement.has(elementUid)) {
        sinkFlowIdsByElement.set(elementUid, new Set<number>());
      }
      sinkFlowIdsByElement.get(elementUid)!.add(flowId);
    }
  }

  const dependencies = new Map<number, Set<number>>();
  const outgoingEdges = new Map<number, Set<number>>();
  const connectedUndirected = new Map<number, Set<number>>();

  for (const flowId of byFlowId.keys()) {
    dependencies.set(flowId, new Set<number>());
    outgoingEdges.set(flowId, new Set<number>());
    connectedUndirected.set(flowId, new Set<number>());
  }

  // Track connected components by any shared element participation (source/sink).
  const allFlowIdsByElement = new Map<string, Set<number>>();
  for (const [elementUid, flowIds] of sourceFlowIdsByElement) {
    if (!allFlowIdsByElement.has(elementUid)) {
      allFlowIdsByElement.set(elementUid, new Set<number>());
    }
    for (const flowId of flowIds) {
      allFlowIdsByElement.get(elementUid)!.add(flowId);
    }
  }
  for (const [elementUid, flowIds] of sinkFlowIdsByElement) {
    if (!allFlowIdsByElement.has(elementUid)) {
      allFlowIdsByElement.set(elementUid, new Set<number>());
    }
    for (const flowId of flowIds) {
      allFlowIdsByElement.get(elementUid)!.add(flowId);
    }
  }

  for (const flowIds of allFlowIdsByElement.values()) {
    const ids = [...flowIds];
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const a = ids[i];
        const b = ids[j];
        connectedUndirected.get(a)!.add(b);
        connectedUndirected.get(b)!.add(a);
      }
    }
  }

  for (const [elementUid, sourceFlowIds] of sourceFlowIdsByElement) {
    const sinkFlowIds = sinkFlowIdsByElement.get(elementUid);
    if (sinkFlowIds == null) continue;

    for (const sourceFlowId of sourceFlowIds) {
      for (const sinkFlowId of sinkFlowIds) {
        if (sourceFlowId == sinkFlowId) continue;
        dependencies.get(sourceFlowId)!.add(sinkFlowId);
        outgoingEdges.get(sinkFlowId)!.add(sourceFlowId);
      }
    }
  }

  const getPriority = (flowId: number) => {
    const sourceElements = sourceElementsByFlow.get(flowId) ?? new Set<string>();
    const sinkElements = sinkElementsByFlow.get(flowId) ?? new Set<string>();

    // Highest priority: flows that originate from the void.
    if (sourceElements.size == 0) return 0;

    // Next: flows that start from elements without configured inflows.
    const allSourcesWithoutInflow = [...sourceElements].every((elementUid) =>
      (inBranchesByElement[elementUid]?.length ?? 0) == 0
    );
    if (allSourcesWithoutInflow) return 1;

    // Prefer outflows into the void as soon as their source element is ready.
    if (sinkElements.size == 0) return 2;

    const hasOutflowFromSinks = [...sinkElements].some((elementUid) =>
      (outBranchesByElement[elementUid]?.length ?? 0) > 0
    );
    return hasOutflowFromSinks ? 3 : 4;
  };

  const sortedFlowIds: number[] = [];
  const visited = new Set<number>();

  // Process each connected flow group as a block.
  const allIds = [...byFlowId.keys()].sort((a, b) => {
    const priorityDiff = getPriority(a) - getPriority(b);
    if (priorityDiff != 0) return priorityDiff;
    return a - b;
  });

  for (const startId of allIds) {
    if (visited.has(startId)) continue;

    const groupIds: number[] = [];
    const queue = [startId];
    visited.add(startId);

    while (queue.length > 0) {
      const flowId = queue.shift()!;
      groupIds.push(flowId);
      for (const neighbor of connectedUndirected.get(flowId) ?? []) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        queue.push(neighbor);
      }
    }

    const groupSet = new Set(groupIds);
    const indegree = new Map<number, number>();
    for (const flowId of groupIds) {
      let count = 0;
      for (const dependencyId of dependencies.get(flowId) ?? []) {
        if (groupSet.has(dependencyId)) {
          count++;
        }
      }
      indegree.set(flowId, count);
    }

    const ready: number[] = groupIds.filter((flowId) => (indegree.get(flowId) ?? 0) == 0);
    const groupOrderedIds: number[] = [];

    while (ready.length > 0) {
      ready.sort((a, b) => {
        const priorityDiff = getPriority(a) - getPriority(b);
        if (priorityDiff != 0) return priorityDiff;
        return a - b;
      });

      const currentId = ready.shift()!;
      groupOrderedIds.push(currentId);

      for (const dependentId of outgoingEdges.get(currentId) ?? []) {
        if (!groupSet.has(dependentId)) continue;

        const nextIndegree = (indegree.get(dependentId) ?? 0) - 1;
        indegree.set(dependentId, nextIndegree);
        if (nextIndegree == 0) {
          ready.push(dependentId);
        }
      }
    }

    // If the graph has cycles, append remaining flows in stable priority order.
    if (groupOrderedIds.length < groupIds.length) {
      const remainingIds = groupIds
        .filter((flowId) => !groupOrderedIds.includes(flowId))
        .sort((a, b) => {
          const priorityDiff = getPriority(a) - getPriority(b);
          if (priorityDiff != 0) return priorityDiff;
          return a - b;
        });
      groupOrderedIds.push(...remainingIds);
    }

    sortedFlowIds.push(...groupOrderedIds);
  }

  return sortedFlowIds
    .map((flowId) => byFlowId.get(flowId))
    .filter((flow): flow is ActivatedFlow => flow != null);
}

export async function showMomentumPopup(
  {
    momentum,
    element,
    round = null
  }: {
    momentum: MomentumItem;
    element: TargetItem | ResourceItem;
    round: SimulationRoundItem | null;
  },
  { checkHideFlag = true } = {}
) {
  const simulationViewStore = useSimulationViewStore();
  if (checkHideFlag && !simulationViewStore.showMomentumMessages) return false;

  return useSimulationMessage().show({
    type: i18n.t("objectNames.momentum"),
    title: i18n.t("labels.momentumOfX", [element.title]),
    item: momentum,
    round,
    ignoreGetter: () => !simulationViewStore.showMomentumMessages,
    ignoreSetter: (val: boolean) =>
      simulationViewStore.setShowMomentumMessages(val)
  });
}

export async function showEndPopup({
  endReason = null,
  round = null
}: {
  endReason: EndReason | null;
  round: SimulationRoundItem | null;
}) {
  if (endReason == null) return;

  const simulationViewStore = useSimulationViewStore();

  // if the simulation was terminated regularly (all rounds played),
  // show the standard ending message.
  if (endReason.type == "ended") {
    // get the text from the scenario
    const scenario = simulationViewStore.scenario;
    let scenarioEndText = scenario?.endText;
    if (Text.isEmpty(scenarioEndText)) {
      const settings = useSettingsStore();
      scenarioEndText = i18n.t("messages.simulationRoundXEnd", [
        scenario?.maxRounds || settings.simulationRoundAmount
      ]);
    }

    return useSimulationMessage().show({
      type: i18n.t("messages.simulationEnded"),
      title: Text.isEmpty(scenario?.endTitle)
        ? i18n.t("messages.simulationEnded")
        : scenario?.endTitle,
      item: {
        type: "simulationEnd",
        text: scenarioEndText,
        imageId: scenario?.endImageId
      },
      round
    });
  }

  // otherwise, show a text for the ending condition
  else if (endReason.type == "aborted" && endReason.endId != null) {
    return simulationViewStore.showScenarioEndPopup({
      scenarioEndId: endReason.endId
    });
  }
}

export async function showIncidentPopup(
  { incident, round }: { incident: IncidentItem; round: SimulationRoundItem },
  { checkHideFlag = true } = {}
) {
  const simulationViewStore = useSimulationViewStore();
  if (checkHideFlag) {
    if (!simulationViewStore.showIncidentMessages) return false;
  }

  return useSimulationMessage().show({
    type: i18n.t("objectNames.incident"),
    title: incident.title,
    item: incident,
    round,
    ignoreGetter: () => !simulationViewStore.showIncidentMessages,
    ignoreSetter: (val: boolean) =>
      simulationViewStore.setShowIncidentMessages(val)
  });
}


function getAffectedItemIds(item: IncidentItem | ActionItem) {
  let effects: EffectItem[] | null = null;

  const effectStore = useEffectStore();

  // load effects
  if (item.type == "incident") {
    effects = effectStore.byIncident[item.id] ?? [];
  } else {
    effects = effectStore.byAction[item.id] ?? [];
  }

  const affectedItemIds = getEffectsAffectedUids(effects);

  // add cost highlights
  if (item.type == "action") {
    for (const cost of item.costs) {
      const uid = cost.elementUid;
      if (!affectedItemIds.has(uid)) {
        affectedItemIds.add(uid);
      }
    }
  }

  return affectedItemIds;
}

export async function showActionPopup(
  { action, round = null }: { action: ActionItem; round: object | null },
  { checkHideFlag = true } = {}
) {
  const simulationViewStore = useSimulationViewStore();
  if (checkHideFlag) {
    if (!simulationViewStore.showActionMessages) return false;
  }

  return useSimulationMessage().show({
    type: i18n.t("objectNames.action"),
    title: action.title,
    item: action,
    round,
    ignoreGetter: () => !simulationViewStore.showActionMessages,
    ignoreSetter: (val: boolean) =>
      simulationViewStore.setShowActionMessages(val)
  });
}

export async function showUnpayableActionPopup(
  { unpayableAction }: { unpayableAction: UnpayableAction },
  { checkHideFlag = true } = {}
) {
  const simulationViewStore = useSimulationViewStore();
  if (checkHideFlag) {
    if (!simulationViewStore.showActionMessages) return false;
  }

  const actionStore = useActionStore();
  const elements = useElements();
  const action = actionStore.items[unpayableAction.actionId];
  const element = elements.byUid[unpayableAction.elementUid];
  const availableValue = unpayableAction.availableValue;
  const needed = unpayableAction.neededValue;
  return showAlert({
    title: i18n.t("errors.notEnoughX", [element?.title || i18n.t("labels.unknown")]),
    text: i18n.t("errors.costsNotMet", {
      action: action.title,
      resource: element?.title || i18n.t("labels.unknown"),
      needed: needed,
      available: availableValue
    })
  });
}

export async function showPreventedActionPopup(
  { preventedAction }: { preventedAction: PreventedAction },
  { checkHideFlag = true } = {}
) {
  const simulationViewStore = useSimulationViewStore();
  if (checkHideFlag) {
    if (!simulationViewStore.showActionMessages) return false;
  }

  const actionStore = useActionStore();
  const action = actionStore.items[preventedAction.actionId];

  // prevented because of condition
  if (preventedAction.conditionIds) {
    return simulationViewStore.showUnfulfilledActionPopup({
      actionId: action.id,
      conditionIds: preventedAction.conditionIds,
      maxOccurrencesReached: false
    });
  } else {
    return simulationViewStore.showUnfulfilledActionPopup({
      actionId: action.id,
      conditionIds: [],
      maxOccurrencesReached: true
    });
  }
}

export async function showEndCard(simulator: Simulation) {
  return showEndPopup({
    endReason: simulator.endReason,
    round: simulator.currentRound
  });
}

function getEffectsAffectedUids(effects: EffectItem[] | Effect[]) {
  return new Set(effects.map((effect) => toUid(effect.affectedType, effect.affectedId)));
}

function wait(time: number) {
  return new Promise((resolve) => {
    const wait = setTimeout(() => {
      clearTimeout(wait);
      resolve(true);
    }, time);
  });
}

function getSourcesByType(impulse: ImpulseItem, type: string) {
  if (!impulse) return [];
  return impulse.sourceValues.filter((source) => source.type == type);
}

function getSource(impulse: ImpulseItem, type: string, id: number | string) {
  if (!impulse) return null;
  return impulse.sourceValues.find((source) => source.type == type && source.id == id);
}
